import { useLocalStorageStore } from '../stores/localStorageStore';

if (typeof window !== 'undefined') {
  window.appWebViewBridge = {
    /**
     * Sets an item in the Zustand store.
     * Key-value pairs are mapped to specific store actions for type safety.
     * @param {string} key - The key of the item to set.
     * @param {string} value - The value to set (should be a string, potentially JSON.stringified).
     * @returns {boolean} - True if successful, false otherwise.
     */
    setItem: (key, value) => {
      try {
        const store = useLocalStorageStore.getState();
        let parsedValue = value;

        // Attempt to parse if it's a JSON string for complex objects
        // For simple strings/numbers stored as strings, direct use might be okay
        // depending on how store actions expect their payloads.
        try {
          parsedValue = JSON.parse(value);
        } catch (e) {
          // Not a JSON string, use as is.
          // This part may need refinement based on the exact data types
          // your store actions expect for each key.
        }

        // console.log(
        //   `[AppWebViewBridge] Attempting to setItem: key=${key}, originalValue=${value}, parsedValue=`,
        //   parsedValue
        // );

        // Map keys to specific store actions
        // This is crucial for type safety and ensuring correct state updates.
        // Add all keys that your Expo app needs to modify.
        switch (key) {
          case 'userData':
            store.setUserData(parsedValue);
            break;
          case 'theme': // theme was consolidated from themePreference
            store.setTheme(
              typeof parsedValue === 'string' ? parsedValue : 'light'
            );
            break;
          case 'newUser': // Stored as string 'true' or null
            store.setNewUser(parsedValue === 'true' ? 'true' : null);
            break;
          case 'isSignupNewUser': // Stored as string 'true' or null
            store.setIsSignupNewUser(parsedValue === 'true' ? 'true' : null);
            break;
          case 'pwaInstallDeeplink': // Key from grep search, matches pwaInstallDeeplink in store
            store.setPwaInstallDeeplink(parsedValue);
            break;
          case 'intendedDestination':
            store.setIntendedDestination(parsedValue);
            break;
          case 'branchData':
            store.setBranchData(parsedValue);
            break;
          case '@first_open': // Key from grep search, matches firstOpen in store
            store.setFirstOpen(parsedValue === 'done' ? 'done' : null);
            break;
          case 'phoneVerifyModalShownTimestamp': // Key from grep search
            store.setPhoneVerifyModalShownTimestamp(String(parsedValue)); // Store as string
            break;
          case 'newsActiveTabIndex':
            store.setNewsActiveTabIndex(String(parsedValue));
            break;
          case 'newsActiveTab':
            store.setNewsActiveTab(String(parsedValue));
            break;
          case 'duelActiveTab':
            store.setDuelActiveTab(String(parsedValue));
            break;
          case 'duelInviteData':
            store.setDuelInviteData(parsedValue); // Expects object or null
            break;
          case 'activeTab': // Used in Practice/TabsComp.jsx
            store.setPracticeActiveTab(
              typeof parsedValue === 'number'
                ? parsedValue
                : parseInt(parsedValue, 10) || 0
            );
            break;
          case 'hasVisitedChatScreen':
            store.setHasVisitedChatScreen(
              parsedValue === 'true' ? 'true' : null
            );
            break;
          case 'chatActiveTab':
            store.setChatActiveTab(
              typeof parsedValue === 'number'
                ? parsedValue
                : parseInt(parsedValue, 10) || 0
            );
            break;
          case 'firstVisit': // Used in Chat/TutorChat/index.jsx
            store.setFirstVisitChat(parsedValue === 'true' ? 'true' : null);
            break;
          case 'lastSparkModalShown':
            store.setLastSparkModalShown(String(parsedValue));
            break;
          case 'isDismissed': // SparkTestModal isDismissed
            store.setIsDismissed(parsedValue === 'true' ? 'true' : null);
            break;
          case 'phone_number':
            store.setPhoneNumber(String(parsedValue));
            break;
          case 'lastCacheClearTime':
            store.setLastCacheClearTime(parsedValue);
            break;
          case 'appVersion':
            store.setAppVersion(parsedValue);
            break;
          case 'questionData':
            store.setQuestionData(parsedValue);
            break;
          case 'userCurrentQuestion':
            store.setUserCurrentQuestion(parsedValue);
            break;
          case 'practiceQuestionStates':
            store.setPracticeQuestionStates(parsedValue);
            break;
          case 'practiceCurrentIndex':
            store.setPracticeCurrentIndex(parsedValue);
            break;
          case 'practiceQuestions':
            store.setPracticeQuestions(parsedValue);
            break;
          case 'newsListScrollPosition':
            store.setNewsListScrollPosition(parsedValue);
            break;
          case 'seenVideos':
            store.markVideoAsSeen(parsedValue);
            break;
          case 'lastVideoTime':
            store.setLastVideoTime(parsedValue);
            break;
          case 'previousStreak':
            store.setPreviousStreak(parsedValue);
            break;
          case 'streakDate':
            store.setStreakDate(parsedValue);
            break;
          case 'completedOnboarding':
            store.setCompletedOnboarding(parsedValue);
            break;
          case 'postLoginRedirect':
            store.setPostLoginRedirect(parsedValue);
            break;
          case 'moeData':
            store.setMoeData(parsedValue);
            break;
          case 'infoHighlightModalShown':
            store.setInfoHighlightModalShown(parsedValue);
            break;
          case 'storedUserId':
            store.setStoredUserId(parsedValue);
            break;
          case 'lastPopupTime':
            store.setLastPopupTime(parsedValue);
            break;
          case 'hasRated':
            store.setHasRated(parsedValue);
            break;
          case 'lastCloseTimeRatingModal':
            store.setLastCloseTimeRatingModal(parsedValue);
            break;
          case 'clearAllStoreData':
            store.clearAllStoreData();
            break;
          default:
            // console.warn(
            //   `[AppWebViewBridge.setItem] No specific action mapped for key: ${key}. Value not set in store.`
            // );
            return false; // Indicate failure if key is not explicitly handled
        }
        // console.log(`[AppWebViewBridge.setItem] Store updated successfully for key: ${key}`);
        return true;
      } catch (e) {
        console.error(
          `[AppWebViewBridge.setItem] Error setting item for key ${key}:`,
          e
        );
        return false;
      }
    },

    /**
     * Removes an item from the Zustand store using the store's removeItem action.
     * @param {string} key - The key of the item to remove.
     * @returns {boolean} - True if successful, false otherwise.
     */
    removeItem: (key) => {
      try {
        useLocalStorageStore.getState().removeItem(key); // Uses the enhanced removeItem in the store
        // console.log(`[AppWebViewBridge.removeItem] Item removed for key: ${key}`);
        return true;
      } catch (e) {
        console.error(
          `[AppWebViewBridge.removeItem] Error removing item for key ${key}:`,
          e
        );
        return false;
      }
    },

    /**
     * Retrieves an item from the Zustand store.
     * @param {string} key - The key of the item to get.
     * @returns {string | undefined} - The value as a JSON string, or undefined if error/not found.
     */
    getItem: (key) => {
      try {
        const state = useLocalStorageStore.getState();
        // Check if the key exists directly on the state object
        if (Object.prototype.hasOwnProperty.call(state, key)) {
          const value = state[key];
          // console.log(`[AppWebViewBridge.getItem] Value for key ${key}:`, value);
          return JSON.stringify(value); // Always return as JSON string
        } else {
          // console.warn(`[AppWebViewBridge.getItem] Key not found in store state: ${key}`);
          return undefined;
        }
      } catch (e) {
        console.error(
          `[AppWebViewBridge.getItem] Error getting item for key ${key}:`,
          e
        );
        return undefined;
      }
    },

    /**
     * Clears all items managed by the localStorageStore.
     * @returns {boolean} - True if successful, false otherwise.
     */
    clear: () => {
      try {
        useLocalStorageStore.getState().clearAllStoreData(); // Uses the new full clear action
        // console.log('[AppWebViewBridge.clear] All app-specific store data cleared via bridge.');
        return true;
      } catch (e) {
        console.error(
          '[AppWebViewBridge.clear] Error clearing store data via bridge:',
          e
        );
        return false;
      }
    },
  };
  // console.log('[AppWebViewBridge] Bridge initialized on window.');
}
